export const FlyoutManager = (() => {
    let openFlyout = null;
    let overlay = null;
    let focusOrigin = null;
    let managedBackgroundElements = [];

    const focusableSelector = [
        'button:not([disabled])',
        '[href]',
        'input:not([disabled]):not([type="hidden"])',
        'select:not([disabled])',
        'textarea:not([disabled])',
        '[tabindex]:not([tabindex="-1"])'
    ].join(", ");

    const preferredFocusSelector = [
        'input:not([disabled]):not([type="hidden"])',
        'select:not([disabled])',
        'textarea:not([disabled])'
    ].join(", ");

    function open(id, trigger = null) {
        const flyout = document.querySelector(`[data-flyout="${id}"]`);
        if (!flyout) return;

        const nextFocusOrigin = resolveFocusOrigin(trigger);
        if (!(openFlyout && nextFocusOrigin && openFlyout.contains(nextFocusOrigin))) {
            focusOrigin = nextFocusOrigin;
        }

        closeAll(false);

        flyout.classList.replace("closed", "open");
        document.body.classList.add("flyout-open");
        overlay?.classList.add("active");

        openFlyout = flyout;
        applyDialogAttributes(flyout);
        setBackgroundInert(flyout);
        focusFlyout(flyout);
        document.addEventListener("keydown", handleKeydown, true);
    }

    function emitClosedEvent(target) {
        if (!target) {
            return;
        }

        const flyoutId = target.getAttribute("data-flyout");
        if (!flyoutId) {
            return;
        }

        document.dispatchEvent(new CustomEvent("flyout:closed", {
            detail: { id: flyoutId }
        }));
    }
    
    function subscribeToClosedEvent(callback) {
        document.addEventListener("flyout:closed", callback);
    }
    
    function unsubscribeToClosedEvent(callback) {
        document.removeEventListener("flyout:closed", callback);
    }

    function close(id = null) {
        const flyout = id
            ? document.querySelector(`[data-flyout="${id}"]`)
            : openFlyout;

        if (flyout) {
            flyout.classList.replace("open", "closed");
            emitClosedEvent(flyout);
        }

        if (!document.querySelector('[data-flyout].open')) {
            cleanupFlyoutState(true);
        }
    }

    function closeAll(shouldRestoreFocus = true) {
        document.querySelectorAll('[data-flyout].open').forEach(el => {
            el.classList.replace("open", "closed");
            emitClosedEvent(el);
        });

        cleanupFlyoutState(shouldRestoreFocus);
    }

    function handleKeydown(e) {
        if (!openFlyout) {
            return;
        }

        if (e.key === "Escape") {
            close();
            return;
        }

        if (e.key !== "Tab") {
            return;
        }

        const focusableElements = getFocusableElements(openFlyout);
        if (focusableElements.length === 0) {
            e.preventDefault();
            openFlyout.focus();
            return;
        }

        const firstElement = focusableElements[0];
        const lastElement = focusableElements[focusableElements.length - 1];
        const activeElement = document.activeElement;

        if (!openFlyout.contains(activeElement)) {
            e.preventDefault();
            (e.shiftKey ? lastElement : firstElement).focus();
            return;
        }

        if (!e.shiftKey && activeElement === lastElement) {
            e.preventDefault();
            firstElement.focus();
        }

        if (e.shiftKey && activeElement === firstElement) {
            e.preventDefault();
            lastElement.focus();
        }
    }

    function initTriggers() {
        overlay = document.getElementById("flyout-overlay");

        document.querySelectorAll('[data-flyout-trigger]').forEach(trigger => {
            trigger.addEventListener("click", (e) => {
                e.preventDefault();
                open(trigger.getAttribute("data-flyout-trigger"), trigger);
            });
        });

        document.querySelectorAll('[data-flyout-close]').forEach(btn => {
            btn.addEventListener("click", () => close());
        });

        overlay?.addEventListener("click", () => close());
    }
    
    function getFlyoutContentContainer(flyoutId) {
        return document.querySelector(`#flyout-${flyoutId} .flyout-container__content`);
    }

    function resolveFocusOrigin(trigger) {
        if (trigger instanceof HTMLElement) {
            return trigger;
        }

        if (document.activeElement instanceof HTMLElement && document.activeElement !== document.body) {
            return document.activeElement;
        }

        return null;
    }

    function applyDialogAttributes(flyout) {
        if (!flyout.hasAttribute("role")) {
            flyout.setAttribute("role", "dialog");
        }

        if (!flyout.hasAttribute("aria-modal")) {
            flyout.setAttribute("aria-modal", "true");
        }

        if (!flyout.hasAttribute("tabindex")) {
            flyout.setAttribute("tabindex", "-1");
        }

        if (!flyout.hasAttribute("aria-labelledby") && !flyout.hasAttribute("aria-label")) {
            const heading = flyout.querySelector("h1, h2, h3, h4, h5, h6");
            if (!heading) {
                return;
            }

            if (!heading.id) {
                const flyoutName = flyout.getAttribute("data-flyout") || flyout.id || "flyout";
                heading.id = `${flyoutName}-title`;
            }

            flyout.setAttribute("aria-labelledby", heading.id);
        }
    }

    function focusFlyout(flyout) {
        const preferredElement = flyout.querySelector(preferredFocusSelector);
        if (preferredElement instanceof HTMLElement) {
            preferredElement.focus();
            return;
        }

        const focusableElements = getFocusableElements(flyout);
        if (focusableElements.length > 0) {
            focusableElements[0].focus();
            return;
        }

        flyout.focus();
    }

    function getFocusableElements(flyout) {
        return Array.from(flyout.querySelectorAll(focusableSelector)).filter(isFocusable);
    }

    function isFocusable(element) {
        if (!(element instanceof HTMLElement)) {
            return false;
        }

        if (element.hasAttribute("disabled")) {
            return false;
        }

        if (element.getAttribute("aria-hidden") === "true") {
            return false;
        }

        if (element.closest("[hidden]")) {
            return false;
        }

        return true;
    }

    function setBackgroundInert(activeFlyout) {
        clearBackgroundInert();

        managedBackgroundElements = Array.from(document.body.children)
            .filter(element => element !== activeFlyout && element !== overlay)
            .map((element) => {
                const previousAriaHidden = element.getAttribute("aria-hidden");
                const hadInert = element.hasAttribute("inert");

                element.setAttribute("inert", "");
                element.setAttribute("aria-hidden", "true");

                return {
                    element,
                    hadInert,
                    previousAriaHidden
                };
            });
    }

    function clearBackgroundInert() {
        managedBackgroundElements.forEach(({ element, hadInert, previousAriaHidden }) => {
            if (!hadInert) {
                element.removeAttribute("inert");
            }

            if (previousAriaHidden === null) {
                element.removeAttribute("aria-hidden");
            } else {
                element.setAttribute("aria-hidden", previousAriaHidden);
            }
        });

        managedBackgroundElements = [];
    }

    function cleanupFlyoutState(shouldRestoreFocus) {
        document.body.classList.remove("flyout-open");
        overlay?.classList.remove("active");
        document.removeEventListener("keydown", handleKeydown, true);
        clearBackgroundInert();

        const elementToRestore = shouldRestoreFocus ? focusOrigin : null;

        openFlyout = null;
        if (shouldRestoreFocus) {
            focusOrigin = null;
        }

        if (
            elementToRestore instanceof HTMLElement &&
            elementToRestore.isConnected &&
            !elementToRestore.hasAttribute("disabled") &&
            !elementToRestore.closest("[inert]")
        ) {
            elementToRestore.focus();
        }
    }

    return { 
        initTriggers, 
        open, 
        close, 
        closeAll, 
        getFlyoutContentContainer, 
        subscribeToClosedEvent,
        unsubscribeToClosedEvent
    };
})();

export function initFlyoutManager() {
    document.addEventListener("DOMContentLoaded", () => {
        FlyoutManager.initTriggers();
    });
}
